/**
 * Zustand store pro PIP měnič.
 *
 *  - `devices`: seznam z DB + runtime stav (status, deviceInfo, settings)
 *  - `telemetry`: poslední ~1 Hz snapshot per slug (živý stream)
 *  - `history`: posledních N (300) snapshotů per slug pro mini-grafy
 *
 * Aktualizováno z WS handlerů v ws-client.ts.
 */

import { create } from "zustand";
import type { PipListItem, PipOverview, PipRuntime, PipStateWsEvent, PipTelemetry } from "@client/types/pip";

const MAX_LIVE_HISTORY = 300;

interface PipStore {
  devices: PipListItem[];
  telemetry: Map<string, PipTelemetry>;
  overview: Map<string, PipOverview>;
  history: Map<string, PipTelemetry[]>;
  loaded: boolean;

  setDevices: (devices: PipListItem[]) => void;
  applyTelemetryFull: (slug: string, t: PipTelemetry) => void;
  applyTelemetryDiff: (slug: string, patch: Partial<PipTelemetry>) => void;
  applyOverviewFull: (slug: string, o: PipOverview) => void;
  applyOverviewDiff: (slug: string, patch: Partial<PipOverview>) => void;
  applyState: (ev: PipStateWsEvent) => void;
  clear: () => void;
}

export const usePipStore = create<PipStore>((set) => ({
  devices: [],
  telemetry: new Map(),
  overview: new Map(),
  history: new Map(),
  loaded: false,

  setDevices: (devices) => set({ devices, loaded: true }),

  applyTelemetryFull: (slug, t) =>
    set((state) => {
      const newTelemetry = new Map(state.telemetry);
      newTelemetry.set(slug, t);
      const newHistory = new Map(state.history);
      const prev = newHistory.get(slug) ?? [];
      newHistory.set(slug, [...prev, t].slice(-MAX_LIVE_HISTORY));
      return { telemetry: newTelemetry, history: newHistory };
    }),

  applyTelemetryDiff: (slug, patch) =>
    set((state) => {
      const prev = state.telemetry.get(slug);
      if (!prev) return state;
      const merged: PipTelemetry = { ...prev, ...patch };
      const newTelemetry = new Map(state.telemetry);
      newTelemetry.set(slug, merged);
      const newHistory = new Map(state.history);
      const prevHist = newHistory.get(slug) ?? [];
      newHistory.set(slug, [...prevHist, merged].slice(-MAX_LIVE_HISTORY));
      return { telemetry: newTelemetry, history: newHistory };
    }),

  applyOverviewFull: (slug, o) =>
    set((state) => {
      const newOverview = new Map(state.overview);
      newOverview.set(slug, o);
      return { overview: newOverview };
    }),

  applyOverviewDiff: (slug, patch) =>
    set((state) => {
      const prev = state.overview.get(slug);
      if (!prev) return state;
      const newOverview = new Map(state.overview);
      newOverview.set(slug, { ...prev, ...patch });
      return { overview: newOverview };
    }),

  applyState: (ev) =>
    set((state) => {
      const devices = state.devices.map((d) => {
        if (d.slug !== ev.slug) return d;
        const runtime: PipRuntime = d.runtime ?? {
          slug: d.slug,
          status: "idle",
          deviceInfo: null,
          settings: null,
          telemetry: null,
          lastPublishedAt: 0,
          lastTelemetryAt: 0,
        };
        return {
          ...d,
          runtime: {
            ...runtime,
            status: ev.status ?? runtime.status,
            deviceInfo: ev.deviceInfo ?? runtime.deviceInfo,
            settings: ev.settings ?? runtime.settings,
          },
        };
      });
      return { devices };
    }),

  clear: () =>
    set({ devices: [], telemetry: new Map(), overview: new Map(), history: new Map(), loaded: false }),
}));
